import React from "react"
import {useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {AKlaraDashboardSalesPersonRow} from "../../../services/types.generated.ts"
import {EMPTY_ARRAY, getFullName} from "../../../utils/index.ts"
import {KlaraFiltersContext} from "../filters.tsx"
import {formatSessionTime} from "../sessions.ts"
import {TDashboardClock, useDashboardClock} from "../useDashboardClock.ts"
import {useKlaraSalesPeople} from "../useKlaraSalesPeople.ts"
import {CardState} from "./CardState.tsx"
import {CARD_CLASS_NAME, CARD_TITLE_CLASS_NAME} from "./cardStyles.ts"

/** Rows shown before "Show all". */
const SALES_PEOPLE_TOP_COUNT = 10

const toPercent = (rate: number) => Math.round(rate * 100)

/**
 * Per-person coverage and updates for the period, in the backend's ranking. A row toggles the sales person filter of
 * the leads table, the feed and the pipeline.
 */
export const SalesPeopleCard: React.FC = () => {
  const {t} = useTranslation()
  const titleId = React.useId()
  const clock = useDashboardClock()
  const {salesPersonExternalId, setSalesPersonExternalId} = KlaraFiltersContext.useContext()
  const [showAll, setShowAll] = React.useState(false)
  const {data, isPending, isError, isFetching, refetch} = useKlaraSalesPeople()
  const response = data?.data
  const rows = response?.sales_people ?? EMPTY_ARRAY
  const visibleRows = showAll ? rows : rows.slice(0, SALES_PEOPLE_TOP_COUNT)
  // The update bars share one scale, so the longest bar is the busiest person.
  const maxUpdates = Math.max(0, ...rows.map(row => row.voice_updates + row.manual_updates))

  const toggle = (externalId: string) =>
    setSalesPersonExternalId(externalId === salesPersonExternalId ? undefined : externalId)

  const teamPercent = response?.team_coverage_rate == null ? null : toPercent(response.team_coverage_rate)

  return (
    <section aria-labelledby={titleId} className={twMerge(CARD_CLASS_NAME, "flex flex-col px-6 py-5")}>
      <div className={"mb-3.5 flex flex-wrap items-baseline gap-x-3 gap-y-1"}>
        <h2 id={titleId} className={twMerge(CARD_TITLE_CLASS_NAME, "flex items-baseline gap-3 whitespace-nowrap")}>
          {t("KlaraDashboard_SalesPeople_Title")}
          {response && (
            <>
              <span className={"sr-only"}> · </span>
              <span className={"rounded-full bg-cr-slate-8 px-2 py-px text-xs font-bold text-cr-blue-dark"}>
                {rows.length}
              </span>
            </>
          )}
        </h2>
        {response && (
          <span
            data-testid={"sales people subtitle"}
            className={"flex grow flex-wrap items-baseline gap-x-3 gap-y-1 text-xs whitespace-nowrap text-cr-slate-60"}
          >
            {t("KlaraDashboard_SalesPeople_RankedBy")}
            <span className={"sr-only"}> · </span>
            <span className={"ml-auto flex items-center gap-1.5 self-center text-[11.5px] text-cr-grey-80"}>
              <span aria-hidden={"true"} className={"block h-3 w-0.5 rounded-[1px] bg-cr-blue-dark"} />
              {teamPercent === null
                ? t("KlaraDashboard_SalesPeople_TeamAverageNone")
                : t("KlaraDashboard_SalesPeople_TeamAverage", {percent: teamPercent})}
            </span>
          </span>
        )}
      </div>
      <CardState query={{isPending, isError, isFetching, refetch}} skeletonClassName={"h-64"}>
        {rows.length === 0 ? (
          <p className={"text-sm text-cr-slate-60"}>{t("KlaraDashboard_SalesPeople_Empty")}</p>
        ) : (
          <div className={"flex flex-col"}>
            <div className={"overflow-x-auto"}>
              <table aria-labelledby={titleId} className={"w-full min-w-[726px] text-left text-[13px]"}>
                {/* Open / won shrinks to its one-line label in any language; the two tracks share the rest. */}
                <colgroup>
                  <col className={"w-[140px]"} />
                  <col className={"w-px"} />
                  <col />
                  <col />
                  <col className={"w-[72px]"} />
                  <col className={"w-28"} />
                </colgroup>
                <thead className={"text-[11px] font-semibold text-cr-slate-60"}>
                  <tr className={"border-b border-cr-slate-15"}>
                    <th className={HEADER_CELL}>{t("KlaraDashboard_SalesPeople_Column_Name")}</th>
                    <th className={twMerge(HEADER_CELL, "text-right")}>
                      {t("KlaraDashboard_SalesPeople_Column_OpenWon")}
                    </th>
                    {/* The track labels take no width, so the two tracks share the spare width equally. */}
                    <th className={HEADER_CELL}>
                      <span className={"inline-block w-0"}>{t("KlaraDashboard_SalesPeople_Column_Coverage")}</span>
                    </th>
                    <th className={HEADER_CELL}>
                      <span className={"inline-block w-0"}>{t("KlaraDashboard_SalesPeople_Column_Updates")}</span>
                    </th>
                    <th className={twMerge(HEADER_CELL, "text-right")}>
                      {t("KlaraDashboard_SalesPeople_Column_Missing")}
                    </th>
                    <th className={twMerge(HEADER_CELL, "text-right")}>
                      {t("KlaraDashboard_SalesPeople_Column_LastUpdate")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(row => (
                    <SalesPersonRow
                      key={row.sales_person.external_id}
                      row={row}
                      maxUpdates={maxUpdates}
                      teamPercent={teamPercent}
                      clock={clock}
                      isActive={row.sales_person.external_id === salesPersonExternalId}
                      onToggle={() => toggle(row.sales_person.external_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {!showAll && rows.length > SALES_PEOPLE_TOP_COUNT && (
              <button
                type={"button"}
                onClick={() => setShowAll(true)}
                className={"mt-2.5 self-start px-2 py-1 text-[12.5px] font-semibold text-cr-blue hover:underline"}
              >
                {t("KlaraDashboard_SalesPeople_ShowAll", {total: rows.length})}
              </button>
            )}
          </div>
        )}
      </CardState>
    </section>
  )
}

const HEADER_CELL = "px-2 pb-2 font-semibold whitespace-nowrap first:pl-2"
const BODY_CELL = "px-2 py-[11px]"

/** Bar colour of a person's coverage: the lower it is, the paler the bar. */
const coverageBarClassName = (percent: number) =>
  percent < 50 ? "bg-cr-indigo-soft" : percent < 70 ? "bg-cr-blue-mid" : "bg-cr-blue"

const SalesPersonRow: React.FC<{
  row: AKlaraDashboardSalesPersonRow
  maxUpdates: number
  teamPercent: number | null
  clock: TDashboardClock
  isActive: boolean
  onToggle: () => void
}> = ({row, maxUpdates, teamPercent, clock, isActive, onToggle}) => {
  const {t} = useTranslation()

  return (
    <tr
      data-testid={`sales person ${row.sales_person.external_id}`}
      tabIndex={0}
      aria-current={isActive ? "true" : undefined}
      onClick={onToggle}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onToggle()
        }
      }}
      className={twMerge(
        "cursor-pointer border-b border-cr-slate-6 hover:bg-cr-slate-1 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-cr-blue",
        isActive && "bg-cr-blue-super-light hover:bg-cr-blue-super-light"
      )}
    >
      <td
        className={twMerge(
          BODY_CELL,
          // Full names wrap rather than being cut short.
          "font-semibold break-words",
          isActive ? "text-cr-blue" : "text-cr-blue-dark"
        )}
      >
        {getFullName(row.sales_person)}
      </td>
      <td className={twMerge(BODY_CELL, "text-right whitespace-nowrap text-cr-grey-80 tabular-nums")}>
        <b className={"text-cr-slate-90"}>{row.open_deals}</b> / <b className={"text-cr-blue"}>{row.won}</b>
      </td>
      <td className={BODY_CELL}>
        <CoverageCell rate={row.coverage_rate} teamPercent={teamPercent} />
      </td>
      <td className={BODY_CELL}>
        <UpdatesCell voice={row.voice_updates} manual={row.manual_updates} max={maxUpdates} />
      </td>
      <td
        className={twMerge(
          BODY_CELL,
          "text-right font-semibold tabular-nums",
          row.pipeline.missing > 0 ? "text-cr-blue-dark" : "text-cr-slate-40"
        )}
      >
        {row.pipeline.missing}
      </td>
      <td className={twMerge(BODY_CELL, "text-right text-xs whitespace-nowrap text-cr-slate-60")}>
        {row.last_update_at === null
          ? t("KlaraDashboard_SalesPeople_NoValue")
          : formatSessionTime(row.last_update_at, {now: clock.asOf, timeZone: clock.timeZone})}
      </td>
    </tr>
  )
}

const CoverageCell: React.FC<{rate: number | null; teamPercent: number | null}> = ({rate, teamPercent}) => {
  const {t} = useTranslation()
  const percent = rate === null ? null : toPercent(rate)

  return (
    <div className={"flex items-center gap-3.5"}>
      <div aria-hidden={"true"} className={"relative h-2.5 min-w-0 grow rounded-full bg-cr-slate-6"}>
        {percent !== null && (
          <div
            className={twMerge("h-full rounded-full", coverageBarClassName(percent))}
            style={{width: `${percent}%`}}
          />
        )}
        {teamPercent !== null && (
          <div
            className={"absolute -top-[3px] h-4 w-0.5 -translate-x-1/2 rounded-[1px] bg-cr-blue-dark"}
            style={{left: `${teamPercent}%`}}
          />
        )}
      </div>
      <span
        data-testid={"person coverage"}
        className={"w-10 shrink-0 text-right font-bold text-cr-slate-90 tabular-nums"}
      >
        {percent === null ? t("KlaraDashboard_SalesPeople_NoValue") : t("KlaraDashboard_Coverage_Value", {percent})}
      </span>
    </div>
  )
}

const UpdatesCell: React.FC<{voice: number; manual: number; max: number}> = ({voice, manual, max}) => {
  const {t} = useTranslation()
  const width = (value: number) => (max === 0 ? 0 : (value / max) * 100)

  return (
    <div className={"flex items-center gap-3.5"}>
      <div
        role={"img"}
        aria-label={t("KlaraDashboard_SalesPeople_UpdatesBreakdown", {voice, manual})}
        className={"flex h-2.5 min-w-0 grow overflow-hidden rounded-full bg-cr-slate-6"}
      >
        <div className={"h-full bg-cr-blue"} style={{width: `${width(voice)}%`}} />
        <div className={"h-full bg-cr-blue-mid-light"} style={{width: `${width(manual)}%`}} />
      </div>
      <span className={"w-10 shrink-0 text-right font-bold text-cr-blue tabular-nums"}>{voice + manual}</span>
    </div>
  )
}
